import {getFeatureFlagStatus} from 'client/components/FeatureFlag/FeatureFlag.react';
import sessionStore from 'client/Session/SessionStore';
import {QuestionModelV3, SubjectModelV2} from 'resources/augmented/Question/QuestionModel.v3';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {getTranslationCoverage, getTranslationSettings} from './Translation.utils';
import {
  fetchTranslatedQuestion,
  fetchTranslatedSupplements,
  getStudentSchoolPermissionsWithoutAssignment,
  getTeacherTranslationPermissions,
  getStudentClassWithSchool,
  fetchQuestionSupplements,
  getAssignmentQuery
} from './Translation.queries';
import {
  ALTERNATE_CONTENT_LANGUAGES,
  AlternateContentLanguage,
  TranslatedQuestion,
  TranslatedSupplement,
  TranslationCoverage
} from './Translation.types';

interface QuestionTranslationState {
  translatedQuestionInfo: TranslatedQuestion | null;
  translatedSupplements: TranslatedSupplement[] | null;
  fetchingTranslations: boolean;
  translationCoverage: TranslationCoverage;
  hasTranslationAccess: boolean;
  isReady: boolean;
  translationSettings: {
    question: boolean;
    supplements: boolean;
  };
  showTranslations: boolean;
  useAssignmentSettings: boolean;
  assignmentSettings: {
    assignment: boolean;
    classroom: boolean;
    student: boolean;
  };
}

interface QuestionTranslationActions {
  fetchTranslation: (
    question: QuestionModelV3,
    subject: SubjectModelV2,
    language?: AlternateContentLanguage
  ) => Promise<void>;
  init: (assignmentId: string | null, isAdaptive?: boolean) => Promise<void>;
  onToggle: () => void;
  reset: () => void;

  // refreshTranslations(): Promise<void>;
}

const initialState: QuestionTranslationState = {
  translatedQuestionInfo: null,
  translatedSupplements: null,
  fetchingTranslations: false,
  translationCoverage: 'none',
  hasTranslationAccess: false,
  useAssignmentSettings: false,
  isReady: false,
  showTranslations: false,
  translationSettings: {
    question: false,
    supplements: false
  },
  assignmentSettings: {
    assignment: false,
    classroom: false,
    student: false
  }
};

export const useQuestionTranslationStore = create<
  QuestionTranslationState & QuestionTranslationActions
>()(
  devtools((set, get) => ({
    ...initialState,
    reset: () => {
      set(initialState);
    },
    init: async (id, isAdaptive = false) => {
      if (sessionStore.isStudent()) {
        const studentId = sessionStore.getUserId();
        let hasPermission = false;
        const [studentFlagEnabled, extendStudentFlagEnabled, settingFlagEnabled] =
          await Promise.all([
            getFeatureFlagStatus('assignment_question_translations'),
            getFeatureFlagStatus('student_question_translations'),
            getFeatureFlagStatus('ell_translation_settings')
          ]);
        if (studentFlagEnabled) {
          if (isAdaptive || (id === undefined && extendStudentFlagEnabled)) {
            hasPermission = await getStudentSchoolPermissionsWithoutAssignment(studentId);
          } else {
            const classroom = await getStudentClassWithSchool(id, studentId);
            const school = classroom?.getSchool();
            hasPermission = school?.isAllowTranslations() || false;

            if (settingFlagEnabled) {
              const classroomId = classroom.getId();
              const assignment = await getAssignmentQuery(
                id,
                classroomId,
                studentId
              ).getResourcePromise();

              set({
                assignmentSettings: {
                  assignment: assignment.getIsAllowTranslationFromSettings(),
                  classroom: assignment.getIsAllowTranslationFromSettings({classroomId}),
                  student: assignment.getIsAllowTranslationFromSettings({classroomId, studentId})
                }
              });
            }
          }
        }

        set({
          isReady: true,
          hasTranslationAccess: studentFlagEnabled && hasPermission,
          useAssignmentSettings: settingFlagEnabled
        });
      } else if (sessionStore.isSchoolAdministrator() || sessionStore.isTeacher()) {
        let hasPermission = true;
        const teacherFlagEnabled = await getFeatureFlagStatus('teacher_question_translations');
        if (teacherFlagEnabled) {
          const teacherId = sessionStore.getUserId();
          hasPermission = await getTeacherTranslationPermissions(teacherId);
        }
        set({
          isReady: true,
          hasTranslationAccess: teacherFlagEnabled && hasPermission
        });
      } else {
        set({
          isReady: true,
          hasTranslationAccess: false
        });
      }
    },
    onToggle: () => {
      const currentStatus = get().showTranslations;
      set({
        showTranslations: !currentStatus
      });
    },
    fetchTranslation: async (question, subject, language = ALTERNATE_CONTENT_LANGUAGES[0]) => {
      if (get().isReady && !get().hasTranslationAccess) {
        // Don't fetch if no access to translation info
        return;
      }

      set({
        fetchingTranslations: true,
        // reset toggles to false
        showTranslations: false
      });
      const supplements = await fetchQuestionSupplements(question.getId());

      const supplementIds = supplements.map((s) => s.getId()).toJS();

      const [{info}, translatedSupplements] = await Promise.all([
        fetchTranslatedQuestion(question.getId(), language),
        fetchTranslatedSupplements(supplementIds)
      ]);

      const translationCoverage = getTranslationCoverage(info, translatedSupplements, {
        question,
        subject,
        supplements
      });

      const translationSettings = getTranslationSettings({
        question,
        subject,
        supplements
      });
      set({
        translatedSupplements,
        translationCoverage, // TODO: change to match api field,
        // reflect this status on FE instead of just a can view toggle
        translatedQuestionInfo: info,
        fetchingTranslations: false,
        translationSettings: {
          question: translationSettings.question,
          supplements: translationSettings.supplement
        }
      });
    }
  }))
);
